import { Chip, Link, Stack, Tooltip, Typography } from "@mui/material";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import UploadIcon from "@mui/icons-material/Upload";
import type { ImportItem } from "../../../../api/types";
import { DateTimeDisplay } from "../../../../components/DateTimeDisplay";
import { DateDisplay } from "../../../../components/DateDisplay";
import { CopyableText } from "../../../../components/CopyableText";
import { AdminTileCard } from "../../../../components/AdminList";
import { useRegistry } from "../../../../context/RegistryContext";
import { ImportActions } from "./ImportActions";

export type ImportTileProps = {
  item: ImportItem;
  onEdit: (item: ImportItem) => void;
  onViewTransactions: (item: ImportItem) => void;
  onViewCsv: (item: ImportItem) => void;
  onOpenDiff: (item: ImportItem) => void;
  onRerun: (item: ImportItem) => void;
  isRerunning?: boolean;
  onDelete?: (item: ImportItem) => void;
  onRestore?: (item: ImportItem) => void;
  onClearFinalized?: (item: ImportItem) => void;
};

export const ImportTile = ({
  item,
  onEdit,
  onViewTransactions,
  onViewCsv,
  onOpenDiff,
  onRerun,
  isRerunning,
  onDelete,
  onRestore,
  onClearFinalized,
}: ImportTileProps) => {
  const { invoiceMap } = useRegistry();
  const invoiceName = item.invoiceId
    ? (invoiceMap[item.invoiceId]?.name ?? item.invoiceId)
    : null;
  const pendingCount = item.pendingDiffCount ?? 0;

  return (
    <AdminTileCard
      footer={
        <ImportActions
          size="small"
          item={item}
          onEdit={onEdit}
          onViewTransactions={onViewTransactions}
          onViewCsv={onViewCsv}
          onOpenDiff={onOpenDiff}
          onRerun={onRerun}
          isRerunning={isRerunning}
          onDelete={onDelete}
          onRestore={onRestore}
          onClearFinalized={onClearFinalized}
        />
      }
    >
      <Link
        component="button"
        type="button"
        variant="subtitle1"
        onClick={() => onEdit(item)}
        aria-label={`Edit import ${item.importerName}`}
        title={item.importerName}
        underline="hover"
        sx={{
          fontWeight: 600,
          minWidth: 0,
          textAlign: "left",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          color: "text.primary",
          minHeight: 32,
        }}
      >
        {item.importerName}
      </Link>
      <Typography variant="caption" color="text.secondary">
        Imported <DateTimeDisplay date={item.dateImported} />
      </Typography>
      {item.firstTransactionDate && item.lastTransactionDate ? (
        <Typography variant="body2" color="text.secondary">
          <DateDisplay date={item.firstTransactionDate} />
          {" – "}
          <DateDisplay date={item.lastTransactionDate} />
        </Typography>
      ) : null}
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
        <Chip
          size="small"
          icon={<RequestQuoteIcon />}
          label={invoiceName ?? "No invoice"}
          color={invoiceName ? "primary" : "default"}
          variant={invoiceName ? "filled" : "outlined"}
          sx={{ height: 32 }}
        />
        {pendingCount > 0 && (
          <Tooltip
            title={
              item.latestPendingDiffAt ? (
                <>
                  Latest pending diff from{" "}
                  <DateTimeDisplay date={item.latestPendingDiffAt} />
                </>
              ) : (
                ""
              )
            }
            disableHoverListener={!item.latestPendingDiffAt}
            disableFocusListener={!item.latestPendingDiffAt}
            disableTouchListener={!item.latestPendingDiffAt}
            arrow
          >
            <Chip
              size="small"
              icon={<UploadIcon />}
              label={`${pendingCount} pending`}
              color="warning"
            />
          </Tooltip>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        <CopyableText text={item.id} maxLength={10} />
      </Typography>
    </AdminTileCard>
  );
};
